/**
 * Signin.jsx - OTP-based Login
 *
 * CHANGES:
 *   - Country code dropdown replaced with react-phone-input-2
 *     (standard plugin, all countries + flags built-in, no DB needed)
 *   - Install: npm install react-phone-input-2
 */

import { useState, useRef, useEffect } from "react";
import { authenticate, triggerSendOtp, triggerVerifyOtp } from "../../Auth/helper";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import LoadingSpinner from "../../components/LoadingSpinner";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const cardStyle = {
  width: "90%", maxWidth: 500, margin: "20px auto",
  background: "#fff", borderRadius: 18,
  boxShadow: "0 4px 32px #e0e0e0", padding: "24px", position: "relative",
};
const buttonStyle = {
  width: "100%", padding: "14px 0", borderRadius: 8,
  background: "#E34607", color: "#fff", fontWeight: 600,
  fontSize: 18, border: "none", marginTop: 16, marginBottom: 8,
  display: "flex", alignItems: "center", justifyContent: "center",
  gap: 8, cursor: "pointer",
};
const subtitleStyle = { color: "#bdbdbd", fontSize: 15, marginBottom: 24 };

// Resend Timer Hook
function useResendTimer(initialSeconds = 300) {
  const [seconds, setSeconds] = useState(0);
  const intervalRef = useRef(null);

  const start = () => {
    setSeconds(initialSeconds);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) { clearInterval(intervalRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearInterval(intervalRef.current), []);
  return { seconds, start };
}

// OTP Input — one box per digit (4 for SMS, 6 for WhatsApp)
function OtpBoxInput({ value, onChange, length = 4 }) {
  const digits = value.split("").concat(Array(length).fill("")).slice(0, length);
  const inputsRef = useRef([]);
  const focusAt = (i) => inputsRef.current[i]?.focus();

  const handleKey = (index, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      onChange(digits.map((d, i) => (i === index ? "" : d)).join(""));
      if (index > 0) focusAt(index - 1);
      return;
    }
    if (!/^\d$/.test(e.key)) return;
    e.preventDefault();
    onChange(digits.map((d, i) => (i === index ? e.key : d)).join(""));
    if (index < length - 1) focusAt(index + 1);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    onChange(pasted);
    focusAt(Math.min(pasted.length, length - 1));
  };

  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 18 }}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (inputsRef.current[i] = el)}
          type="text" inputMode="numeric" maxLength={1}
          value={d} onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste} onChange={() => {}}
          style={{
            // Shrinks below 56px so 6 boxes still fit on narrow phones
            flex: "0 1 56px", minWidth: 0, height: 60, textAlign: "center",
            fontSize: 24, fontWeight: 700, borderRadius: 10,
            border: d ? "2px solid #E34607" : "1.5px solid #e0e0e0",
            background: d ? "#fff5f2" : "#fafbfc",
            outline: "none", transition: "border 0.15s, background 0.15s",
          }}
        />
      ))}
    </div>
  );
}

const Signin = () => {
  const [step, setStep] = useState("mobile");

  // phone holds full number with dial code e.g. "919876543210"
  // dialCode holds just the prefix e.g. "91"
  const [phone, setPhone] = useState("91"); // default India
  const [dialCode, setDialCode] = useState("91");

  const [otp, setOtp] = useState("");
  const [otpChannel, setOtpChannel] = useState("sms"); // "whatsapp" | "sms" — set from backend response
  const [otpLength, setOtpLength] = useState(4);       // 6 for WhatsApp, 4 for SMS — set from backend response
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { seconds: resendSeconds, start: startResendTimer } = useResendTimer(300);
  const Navigate = useNavigate();

  // Extract just the local number (without dial code)
  const localNumber = phone.startsWith(dialCode)
    ? phone.slice(dialCode.length)
    : phone;

  // Step 1: Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!localNumber || localNumber.length < 6) {
      setErrorMessage("Please enter a valid mobile number.");
      return;
    }

    setIsLoading(true);
    try {
      // Local number is the DB key (stored without dial code); dialCode is needed for WhatsApp
      const result = await triggerSendOtp(localNumber, dialCode);
      if (result?.error) {
        setErrorMessage(result.error);
      } else {
        const channel = result?.channel === "whatsapp" ? "whatsapp" : "sms";
        setOtpChannel(channel);
        setOtpLength(result?.otpLength || 4);
        setStep("otp");
        setSuccessMessage(`OTP sent to +${dialCode} ${localNumber}${channel === "whatsapp" ? " on WhatsApp" : ""}`);
        startResendTimer();
      }
    } catch {
      setErrorMessage("Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setErrorMessage(""); setSuccessMessage("");
    if (otp.replace(/\D/g, "").length !== otpLength) {
      setErrorMessage(`Please enter the complete ${otpLength}-digit OTP.`);
      return;
    }
    setIsLoading(true);
    try {
      const result = await triggerVerifyOtp(localNumber, otp);
      if (result?.error) { setErrorMessage(result.error); return; }
      if (result?.isNewUser) {
        Navigate("/signup", { state: { MobileNo: localNumber, otpVerified: true } });
        return;
      }
      authenticate(result);
      Navigate("/");
    } catch {
      setErrorMessage("Failed to verify OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    if (resendSeconds > 0) return;
    setErrorMessage(""); setSuccessMessage(""); setOtp("");
    setIsLoading(true);
    try {
      const result = await triggerSendOtp(localNumber, dialCode);
      if (result?.error) { setErrorMessage(result.error); }
      else {
        const channel = result?.channel === "whatsapp" ? "whatsapp" : "sms";
        setOtpChannel(channel);
        setOtpLength(result?.otpLength || 4);
        setSuccessMessage(channel === "whatsapp" ? "OTP resent on WhatsApp!" : "OTP resent successfully!");
        startResendTimer();
      }
    } catch { setErrorMessage("Failed to resend OTP."); }
    finally { setIsLoading(false); }
  };

  return (
    <>
      <Header />
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", background: "#fafbfc",
        padding: "20px", boxSizing: "border-box",
      }}>
        <div style={cardStyle}>
          <h2 style={{ fontWeight: 700, fontSize: "28px", marginBottom: 0 }}>
            {step === "mobile" ? "Sign In" : "Enter OTP"}
          </h2>

          {step === "mobile" && (
            <>
              <div style={subtitleStyle}>Enter your mobile number to receive an OTP</div>
              <div style={{
                display: "inline-block", background: "#fff5f2",
                border: "1px solid #E34607", color: "#E34607",
                borderRadius: 8, padding: "6px 14px",
                fontSize: 13, fontWeight: 600, marginBottom: 20,
              }}>
                NO PASSWORD REQUIRED
              </div>
            </>
          )}

          {step === "otp" && (
            <div style={subtitleStyle}>
              OTP sent to +{dialCode} {localNumber}{otpChannel === "whatsapp" ? " on WhatsApp" : ""}
              <span style={{ display: "block", fontSize: 12, color: "#E34607", marginTop: 4, fontWeight: 500 }}>
                Valid for 5 minutes
              </span>
            </div>
          )}

          {/* Step 1: Phone input with react-phone-input-2 */}
          {step === "mobile" && (
            <form autoComplete="off" onSubmit={handleSendOtp}>
              <div style={{ marginBottom: 18 }}>
                {/* react-phone-input-2 — handles all countries + flags */}
                <PhoneInput
                  country="in"
                  value={phone}
                  onChange={(value, data) => {
                    setPhone(value);
                    setDialCode(data.dialCode);
                  }}
                  inputStyle={{
                    width: "100%", height: 48,
                    fontSize: 16, borderRadius: 12,
                    border: "1px solid #e0e0e0",
                    background: "#fafbfc",
                    paddingLeft: 52,
                  }}
                  buttonStyle={{
                    borderRadius: "12px 0 0 12px",
                    border: "1px solid #e0e0e0",
                    background: "#fafbfc",
                  }}
                  dropdownStyle={{ borderRadius: 10, zIndex: 9999 }}
                  enableSearch={true}
                  searchPlaceholder="Search country..."
                  preferredCountries={["in", "us", "gb", "lk"]}
                />
              </div>

              <button type="submit" style={buttonStyle} disabled={isLoading}>
                {isLoading
                  ? <LoadingSpinner size="1x" color="#fff" text="Sending OTP..." showText={true} />
                  : <>Send OTP <span style={{ fontSize: 20, marginLeft: 8 }}>&#8594;</span></>}
              </button>
            </form>
          )}

          {/* Step 2: OTP boxes */}
          {step === "otp" && (
            <form autoComplete="off" onSubmit={handleVerifyOtp}>
              <OtpBoxInput value={otp} onChange={setOtp} length={otpLength} />
              <button type="submit" style={buttonStyle} disabled={isLoading}>
                {isLoading
                  ? <LoadingSpinner size="1x" color="#fff" text="Verifying..." showText={true} />
                  : <>Verify OTP <span style={{ fontSize: 20, marginLeft: 8 }}>&#10003;</span></>}
              </button>

              <div style={{ textAlign: "center", marginTop: 12 }}>
                {resendSeconds > 0 ? (
                  <span style={{ color: "#bdbdbd", fontSize: 14 }}>
                    Resend OTP in <strong style={{ color: "#E34607" }}>
                      {Math.floor(resendSeconds / 60)} min {resendSeconds % 60}s
                    </strong>
                  </span>
                ) : (
                  <span style={{ color: "#E34607", cursor: "pointer", fontWeight: 500, fontSize: 14 }}
                    onClick={handleResend}>
                    Resend OTP
                  </span>
                )}
              </div>

              <div style={{ textAlign: "center", marginTop: 8 }}>
                <span style={{ color: "#bdbdbd", cursor: "pointer", fontSize: 13 }}
                  onClick={() => { setStep("mobile"); setOtp(""); setErrorMessage(""); setSuccessMessage(""); }}>
                  Change mobile number
                </span>
              </div>
            </form>
          )}

          {errorMessage && (
            <div className="alert alert-warning fade show" role="alert" style={{ marginTop: 12 }}>
              <strong>{errorMessage}</strong>
            </div>
          )}
          {successMessage && (
            <div className="alert alert-success fade show" role="alert" style={{ marginTop: 12 }}>
              <strong>{successMessage}</strong>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Signin;